<template>
  <Teleport to="body">
    <div class="wl-editor" :class="{ 'wl-editor--right-open': rightPanelVisible }">
      <header class="wl-topbar" @pointerdown.stop>
        <div class="wl-topbar__left">
          <button type="button" class="wl-icon-btn" title="返回航线库" @click="requestClose"><ArrowLeftOutlined /></button>
          <a-tooltip :title="hasErrors(issues) ? '存在错误，无法保存' : '保存到航线库'">
            <button type="button" class="wl-icon-btn" :disabled="hasErrors(issues) || saving" @click="save"><LoadingOutlined v-if="saving" /><SaveOutlined v-else /></button>
          </a-tooltip>
          <a-tooltip title="下载 KMZ 文件">
            <button type="button" class="wl-icon-btn" :disabled="hasErrors(issues)" @click="exportKmz"><DownloadOutlined /></button>
          </a-tooltip>
          <a-dropdown v-model:visible="routeSettingsOpen" :trigger="['click']" placement="bottomLeft" overlay-class-name="wl-route-settings-dropdown">
            <button type="button" class="wl-topbar__settings" :class="{ active: routeSettingsOpen }">
              <SlidersOutlined /> 航线设置 <CaretUpOutlined v-if="routeSettingsOpen" /><CaretDownOutlined v-else />
            </button>
            <template #overlay><WaylineRouteSettings /></template>
          </a-dropdown>
        </div>
        <div class="wl-topbar__center">
          <input v-if="renaming" ref="renameInput" class="wl-topbar__rename" :value="draft.name" :maxlength="WAYLINE_NAME_MAX_LENGTH" @blur="finishRename" @keydown.enter.prevent="finishRename" @keydown.esc.prevent="renaming = false" />
          <button v-else type="button" class="wl-topbar__name" title="点击重命名航线" @click="startRename">{{ draft.name }}</button>
          <span class="wl-topbar__meta"><RocketOutlined /> {{ droneProfile?.name || draft.droneModelKey }}</span>
          <span class="wl-topbar__meta"><CameraOutlined /> {{ payloadProfile?.name || draft.payloadModelKey }}</span>
          <span v-if="draft.dock" class="wl-topbar__meta"><HomeOutlined /> {{ draft.dock.name }}</span>
          <span v-if="sourceWaylineId" class="wl-topbar__tag">编辑已有航线</span>
        </div>
        <div class="wl-topbar__right">
          <a-popover trigger="click" placement="bottomRight" overlay-class-name="wl-popover">
            <template #content>
              <div class="wl-issues">
                <h4>航线检查</h4>
                <p v-if="!issues.length && !nearGround.length" class="wl-muted">未发现问题。</p>
                <div v-for="(issue, index) in issues" :key="'i' + index" class="wl-issues__item" :class="issue.level" @click="issue.index !== undefined && focusIssue(issue.index)">
                  <ExclamationCircleFilled /><span>{{ issue.message }}</span>
                </div>
                <div v-for="issue in nearGround" :key="'g' + issue.index" class="wl-issues__item warning" @click="focusIssue(issue.index)">
                  <ExclamationCircleFilled /><span>【近地提示】{{ issue.index + 1 }}# 航点高度距离参考地形 {{ issue.agl.toFixed(1) }} m，小于 {{ settings.minAgl }} m。</span>
                </div>
              </div>
            </template>
            <button type="button" class="wl-icon-btn" title="航线检查">
              <a-badge :count="issues.length + nearGround.length" :number-style="{ backgroundColor: hasErrors(issues) ? '#ff4d4f' : '#ff9f1a', boxShadow: 'none' }" :offset="[4, -2]"><BellOutlined /></a-badge>
            </button>
          </a-popover>
          <a-popover trigger="click" placement="bottomRight" overlay-class-name="wl-popover">
            <template #content><WaylineDisplaySettings /></template>
            <button type="button" class="wl-icon-btn" title="显示与编辑设置"><SettingOutlined /></button>
          </a-popover>
          <button type="button" class="wl-icon-btn" title="快捷键说明 (?)" @click="shortcutsOpen = !shortcutsOpen">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="2.5" y="6" width="19" height="12" rx="2" /><path d="M6 10h1M9.5 10h1M13 10h1M16.5 10h1M6 14h1M9.5 14h5M16.5 14h1" stroke-linecap="round" /></svg>
          </button>
        </div>
      </header>

      <WaypointListPanel class="wl-left" @pointerdown.stop />

      <div class="wl-map">
        <div v-if="nearGround.length" class="wl-warning" @pointerdown.stop>
          <button type="button" class="wl-warning__chip" @click="focusIssue(nearGround[0].index)">
            <ExclamationCircleFilled />
            <span>【近地提示】{{ nearGround[0].index + 1 }}# 航点高度距离参考地形小于{{ settings.minAgl }} m（{{ (settings.minAgl * 3.281).toFixed(1) }}ft）</span>
            <b v-if="nearGround.length > 1" class="wl-warning__more">{{ nearGround.length }}</b>
          </button>
        </div>
        <p v-else-if="toolHint" class="wl-tool-hint">{{ toolHint }}</p>
        <p v-if="!terrainAvailable" class="wl-terrain-note" :class="{ 'wl-terrain-note--below': nearGround.length }" title="工作空间未配置真实地形。地图按参考起飞点所在平面显示相对高度；显示的净空不能作为真实地形依据。">未配置真实地形，按参考起飞点平面显示</p>
        <button
          v-else-if="padHeightGap !== null && Math.abs(padHeightGap) > PAD_GAP_WARNING"
          type="button"
          class="wl-terrain-note wl-terrain-note--button"
          :class="{ 'wl-terrain-note--below': nearGround.length }"
          :title="`${draft.dock ? '机场' : '起飞点'}上报椭球高 ${draft.missionConfig.takeOffRefPoint!.ellipsoidHeight!.toFixed(1)} m，该处地形 ${padTerrain!.toFixed(1)} m。地图已把起飞点放在地形上显示。若机场安装在屋顶，请在“航线设置”填写起飞点离地高度；否则请检查机场 RTK/定位。`"
          @click="routeSettingsOpen = true"
        >起飞点上报高度比地形{{ padHeightGap > 0 ? '高' : '低' }} {{ Math.abs(padHeightGap).toFixed(1) }} m，已按地形显示</button>

        <div v-if="draft.templateType === 'waypoint'" class="wl-map-actions" @pointerdown.stop>
          <span class="wl-map-actions__title">基于飞行器位置</span>
          <template v-if="aircraft.active">
            <button type="button" class="wl-action" @click="addWaypointAtAircraft"><span class="wl-action__icon"><PlusOutlined /></span><span>新增航点<small>[Space]</small></span></button>
            <button type="button" class="wl-action" @click="orientedShotAtAircraft"><span class="wl-action__icon"><CameraOutlined /></span><span>定向拍照<small>[F]</small></span></button>
            <button type="button" class="wl-action" :disabled="droneProfile && !droneProfile.supportsPano" @click="panoShotAtAircraft"><span class="wl-action__icon"><GlobalOutlined /></span><span>全景拍照</span></button>
            <button type="button" class="wl-action" @click="recordAttitudeAtAircraft"><span class="wl-action__icon"><CompassOutlined /></span><span>记录当前姿态</span></button>
            <a-dropdown :trigger="['click']" placement="bottomLeft" overlay-class-name="wl-menu">
              <button type="button" class="wl-action"><span class="wl-action__icon"><EllipsisOutlined /></span><span>更多</span></button>
              <template #overlay>
                <a-menu @click="actionAtAircraft($event.key)">
                  <a-menu-item v-for="type in moreActionTypes" :key="type">{{ ACTION_LABELS[type] }}</a-menu-item>
                </a-menu>
              </template>
            </a-dropdown>
            <button type="button" class="wl-action wl-action--danger" @click="landAircraft"><span class="wl-action__icon"><PoweroffOutlined /></span><span>结束模拟</span></button>
          </template>
          <template v-else>
            <button type="button" class="wl-action wl-action--primary" @click="takeOff"><span class="wl-action__icon"><SendOutlined /></span><span>模拟起飞</span></button>
            <button type="button" class="wl-action" :class="{ active: tool === 'add' }" @click="setTool('add')"><span class="wl-action__icon"><PlusOutlined /></span><span>点击地图添加</span></button>
            <button type="button" class="wl-action" :class="{ active: tool === 'takeoff' }" @click="setTool('takeoff')"><span class="wl-action__icon"><FlagOutlined /></span><span>设置起飞点</span></button>
          </template>
        </div>
        <div v-else class="wl-map-actions" @pointerdown.stop>
          <span class="wl-map-actions__title">测绘区域</span>
          <button type="button" class="wl-action" :class="{ active: tool === 'add' }" @click="setTool('add')"><span class="wl-action__icon"><PlusOutlined /></span><span>绘制区域顶点</span></button>
          <button type="button" class="wl-action" :disabled="!draft.mapping.vertices.length" @click="clearVertices"><span class="wl-action__icon"><DeleteOutlined /></span><span>清空区域</span></button>
          <button type="button" class="wl-action" :class="{ active: tool === 'takeoff' }" @click="setTool('takeoff')"><span class="wl-action__icon"><FlagOutlined /></span><span>设置起飞点</span></button>
        </div>

        <div class="wl-map-tools" @pointerdown.stop>
          <button type="button" class="wl-compass" :title="`地图朝向 ${headingLabel(cameraHeading)}，点击回正`" @click="resetNorth">
            <svg viewBox="0 0 48 48" width="48" height="48">
              <circle cx="24" cy="24" r="22" fill="#141414d9" stroke="#5a5a5e" />
              <g :transform="`rotate(${-cameraHeading} 24 24)`">
                <path d="M24 5 L29 24 L24 20 L19 24 Z" fill="#ff4d4f" />
                <path d="M24 43 L29 24 L24 28 L19 24 Z" fill="#e8e8e8" />
                <text x="24" y="12" text-anchor="middle" font-size="7" fill="#fff" font-weight="700">N</text>
              </g>
            </svg>
            <span>{{ headingLabel(cameraHeading) }}</span>
          </button>
          <div class="wl-map-tools__group">
            <button type="button" :class="{ active: settings.flat }" title="切换 2D / 3D 视角" @click="setFlat(!settings.flat)">{{ settings.flat ? '3D' : '2D' }}</button>
            <button v-if="draft.templateType === 'waypoint'" type="button" :class="{ active: viewMode === 'follow' }" :disabled="!aircraft.active" title="视角跟随飞行器 (V)" @click="setViewMode(viewMode === 'follow' ? 'free' : 'follow')"><AimOutlined /></button>
            <button v-if="draft.templateType === 'waypoint'" type="button" :class="{ active: viewMode === 'fpv' }" :disabled="!aircraft.active" title="第一视角 (~)" @click="setViewMode(viewMode === 'fpv' ? 'follow' : 'fpv')"><VideoCameraOutlined /></button>
            <button type="button" title="定位到整条航线" @click="fitRoute"><FullscreenOutlined /></button>
            <button type="button" title="定位到我的位置" @click="locateMe"><EnvironmentOutlined /></button>
          </div>
          <div class="wl-map-tools__group">
            <button type="button" title="放大" @click="zoom(1)"><PlusOutlined /></button>
            <button type="button" title="缩小" @click="zoom(-1)"><MinusOutlined /></button>
          </div>
        </div>

        <div v-if="aircraft.active" class="wl-bottom"><WaylineFlightHud class="wl-hud" @pointerdown.stop /></div>
        <WaylineCameraPreview v-if="aircraft.active" class="wl-camera" @pointerdown.stop />
      </div>

      <WaypointActionPanel v-if="rightPanelVisible" class="wl-right" @pointerdown.stop />
      <WaylineShortcutsOverlay v-if="shortcutsOpen" @close="shortcutsOpen = false" />
    </div>
  </Teleport>
</template>

<script lang="ts" setup>
/* global defineEmits, defineProps */
import { computed, nextTick, onBeforeUnmount, onMounted, provide, reactive, ref, watch } from 'vue'
import { message, Modal } from 'ant-design-vue'
import {
  AimOutlined, ArrowLeftOutlined, BellOutlined, CameraOutlined, CaretDownOutlined, CaretUpOutlined, CompassOutlined, DeleteOutlined, DownloadOutlined,
  EllipsisOutlined, EnvironmentOutlined, ExclamationCircleFilled, FlagOutlined, FullscreenOutlined, GlobalOutlined, HomeOutlined, LoadingOutlined,
  MinusOutlined, PlusOutlined, PoweroffOutlined, RocketOutlined, SaveOutlined, SendOutlined, SettingOutlined, SlidersOutlined, VideoCameraOutlined,
} from '@ant-design/icons-vue'
import type { DockOption, DraftWaypoint, EditorHeightMode, PayloadLens, WaylineDraft, WaypointAction, WaypointActionType } from '/@/types/wayline-editor'
import type { EditorEvent, EditorMarker, EditorPolyline, EditorShapes } from '/@/utils/cesium-scene'
import {
  ACTION_LABELS, AIRCRAFT_SPEED_MODES, WAYLINE_DRAFT_KEY, WAYLINE_NAME_MAX_LENGTH, absoluteHeight, aircraftAtWaypoint, bearing, cameraFootprint, cameraSpec,
  cloneDraft, convertHeightMode, createAction, createVertex, createWaypoint, destinationPoint, findDroneProfile, findPayloadProfile, generateMappingRoute, hasErrors,
  haversineDistance, headingLabel, nearGroundIssues, reverseWaypoints, routeStatistics, signedHeading, stepVirtualAircraft, validateDraft, waypointHeight,
  DEFAULT_MIN_AGL, buildDockOption,
} from '/@/utils/wayline-editor'
import { buildKmzFile } from '/@/utils/wayline-kmz-writer'
import { loadGeoidModel, preloadGeoidModel } from '/@/utils/geoid'
import {
  cesiumMapState, cesiumSceneApi, clearCesiumEditor, flyCesiumTo, focusCesiumEditor, followCesiumPosition, setCesiumEditorInteraction, setCesiumEditorShapes, setCesiumTrail,
} from '/@/store/cesium-map'
import { deleteWaylineFile, importKmzFile } from '/@/api/wayline'
import { getBindingDevices } from '/@/api/manage'
import { EDeviceTypeName, ELocalStorageKey } from '/@/types/enums'
import { useMyStore } from '/@/store'
import { downloadFile } from '/@/utils/common'
import { sanitizeWaylineName } from '/@/utils/flight-record'
import { WAYLINE_EDITOR_KEY } from './editor-context'
import type { AircraftState, DisplaySettings, EditorTool, ViewMode, WaylineEditorContext } from './editor-context'
import WaypointListPanel from './WaypointListPanel.vue'
import WaylineRouteSettings from './WaylineRouteSettings.vue'
import WaypointActionPanel from './WaypointActionPanel.vue'
import WaylineFlightHud from './WaylineFlightHud.vue'
import WaylineCameraPreview from './WaylineCameraPreview.vue'
import WaylineDisplaySettings from './WaylineDisplaySettings.vue'
import WaylineShortcutsOverlay from './WaylineShortcutsOverlay.vue'

const props = defineProps<{ draft: WaylineDraft; sourceWaylineId?: string }>()
const emit = defineEmits(['close', 'saved'])

const store = useMyStore()
const workspaceId = localStorage.getItem(ELocalStorageKey.WorkspaceId)!
const draft = reactive(cloneDraft(props.draft)) as WaylineDraft
provide(WAYLINE_DRAFT_KEY, draft)

// ---------------------------------------------------------------------------
// State

const selectedIds = ref<string[]>([])
const activeActionIndex = ref(-1)
const tool = ref<EditorTool>('select')
const poiTarget = ref<'global' | 'selected'>('global')
const viewMode = ref<ViewMode>('follow')
const saving = ref(false)
const dirty = ref(false)
const history = ref<string[]>([])
const actionTemplate = ref<WaypointAction[]>([])
const docks = ref<DockOption[]>([])
const loadingDocks = ref(false)
const routeSettingsOpen = ref(false)
const shortcutsOpen = ref(false)
const renaming = ref(false)
const renameInput = ref<HTMLInputElement | null>(null)
const rightPanelOpen = ref(true)
const cameraHeading = ref(0)
const groundHeights = ref<Record<string, number | null>>({})
const aircraftGround = ref<number | null>(null)
const terrainAvailable = ref(true)
const padTerrain = ref<number | null>(null)
let padTerrainKey = ''
/** Reported pad heights this far off the terrain get a warning (dock RTK drift or a rooftop). */
const PAD_GAP_WARNING = 15
const pressedKeys = ref<Record<string, boolean>>({})
const myPosition = ref<{ longitude: number; latitude: number } | null>(null)
const settings = reactive<DisplaySettings>({ showWaypoints: true, showGimbal: true, showDropLines: true, thickLines: false, recordAttitude: true, minAgl: DEFAULT_MIN_AGL, flat: false })
const aircraft = reactive<AircraftState>({ active: false, longitude: 0, latitude: 0, height: 0, heading: 0, gimbalPitch: -30, speedMode: 'normal', lens: 'wide' })
/** Path flown since takeoff, in the draft height mode; rendered as the trail. */
const trail = ref<Array<[number, number, number]>>([])
let animationFrame = 0
let lastFrameTime = 0
let groundTimer: ReturnType<typeof setInterval> | null = null
let dragSnapshot: string | null = null
/** Most detailed terrain sample per waypoint, keyed by id with the position it was taken at. */
const groundCache = new Map<string, { key: string; height: number | null }>()
let groundSampleTimer: ReturnType<typeof setTimeout> | null = null

const droneProfile = computed(() => findDroneProfile(draft.droneModelKey))
const payloadProfile = computed(() => findPayloadProfile(draft.droneModelKey, draft.payloadModelKey))
const heightMode = computed(() => draft.templateType === 'waypoint' ? draft.waypoint.heightMode : draft.mapping.heightMode)
const selected = computed<DraftWaypoint[]>(() => draft.waypoint.waypoints.filter(item => selectedIds.value.includes(item.id)))
const generated = computed(() => draft.templateType === 'mapping2d' ? generateMappingRoute(draft) : null)
const stats = computed(() => routeStatistics(draft, generated.value))
const issues = computed(() => validateDraft(draft))
/**
 * Drawing frame. The map stands the pad on the terrain under it plus its
 * declared height above ground, and keeps every route height at its offset
 * from the pad. A dock reports its own ellipsoid height, which can sit tens
 * of metres off the terrain data (RTK base convergence, or different data),
 * and on the bare reference ellipsoid the heights have nothing to stand on
 * at all: drawn as they are, the pad and the whole route hang in the air.
 * The draft itself always keeps the reported ellipsoid heights.
 */
const sceneOffset = computed(() => {
  const pad = draft.missionConfig.takeOffRefPoint
  if (!pad || pad.ellipsoidHeight === null) return 0
  const agl = pad.aglHeight ?? 0
  if (!terrainAvailable.value) return pad.ellipsoidHeight - agl
  return padTerrain.value === null ? 0 : pad.ellipsoidHeight - (padTerrain.value + agl)
})
const padHeightGap = computed(() => {
  const pad = draft.missionConfig.takeOffRefPoint
  if (!pad || pad.ellipsoidHeight === null || !terrainAvailable.value || padTerrain.value === null) return null
  return pad.ellipsoidHeight - (padTerrain.value + (pad.aglHeight ?? 0))
})
const toScene = (height: number | null): number | null => height === null ? null : height - sceneOffset.value
const fromScene = (height: number): number => height + sceneOffset.value
/** Terrain under each waypoint in draft (ellipsoid) metres. */
const waypointTerrain = computed(() => {
  const result: Record<string, number | null> = {}
  Object.entries(groundHeights.value).forEach(([id, height]) => { result[id] = height === null ? null : fromScene(height) })
  return result
})
const nearGround = computed(() => nearGroundIssues(draft, waypointTerrain.value, settings.minAgl))
const aircraftAgl = computed(() => {
  const absolute = toScene(absoluteHeight(draft, aircraft.height, heightMode.value))
  const surface = aircraftGround.value ?? toScene(draft.missionConfig.takeOffRefPoint?.ellipsoidHeight ?? null)
  return absolute !== null && surface !== null ? absolute - surface : aircraft.height
})
const canUndo = computed(() => history.value.length > 0)
const hasActionTemplate = computed(() => actionTemplate.value.length > 0)
const rightPanelVisible = computed(() => draft.templateType === 'waypoint' && selected.value.length > 0 && rightPanelOpen.value)
const moreActionTypes: WaypointActionType[] = ['takePhoto', 'startRecord', 'stopRecord', 'hover', 'focus', 'zoom', 'customDirName', 'startTimedShot', 'startDistanceShot', 'endIntervalShot']
const heightRange = computed<[number, number]>(() => heightMode.value === 'WGS84' ? [-500, 1500] : [2, 1500])
const toolHint = computed(() => {
  if (tool.value === 'takeoff') return '点击地图设置参考起飞点'
  if (tool.value === 'poi') return poiTarget.value === 'global' ? '点击地图设置全局兴趣点' : '点击地图设置所选航点的兴趣点'
  if (tool.value === 'add') return draft.templateType === 'waypoint' ? '点击地图添加航点，拖动航点可移动' : '点击地图添加区域顶点，拖动顶点可调整'
  if (draft.templateType === 'mapping2d' && draft.mapping.vertices.length < 3) return '选择“绘制区域顶点”后在地图上依次点击'
  return ''
})

// ---------------------------------------------------------------------------
// Map shapes

const shapes = computed<EditorShapes>(() => {
  const markers: EditorMarker[] = []
  const polylines: EditorPolyline[] = []
  const polygons: EditorShapes['polygons'] = []
  const takeoff = draft.missionConfig.takeOffRefPoint
  const toAbsolute = (height: number) => toScene(absoluteHeight(draft, height, heightMode.value))
  const ground = toScene(takeoff?.ellipsoidHeight ?? null)
  /** Sampled terrain height for a ground marker; null clamps it until the sample is in. */
  const surface = (id: string): number | null => groundHeights.value[id] ?? null
  const lineWidth = settings.thickLines ? 7 : 4
  if (aircraft.active) {
    const absolute = toAbsolute(aircraft.height)
    const agl = aircraftAgl.value
    const nose = destinationPoint(aircraft, aircraft.heading, 10)
    markers.push({ id: 'aircraft', position: [aircraft.longitude, aircraft.latitude, absolute], label: '', color: '#00d4ff', selected: false, size: 12, draggable: false })
    markers.push({ id: 'aircraft-shadow', position: [aircraft.longitude, aircraft.latitude, aircraftGround.value], label: `${agl.toFixed(1)} m`, color: '#ffffff', selected: false, size: 6, draggable: false })
    polylines.push({ id: 'aircraft-heading', positions: [[aircraft.longitude, aircraft.latitude, absolute], [nose.longitude, nose.latitude, absolute]], color: '#00d4ff', width: 4, dashed: false })
    if (absolute !== null && ground !== null && settings.showDropLines) {
      polylines.push({ id: 'aircraft-drop', positions: [[aircraft.longitude, aircraft.latitude, absolute], [aircraft.longitude, aircraft.latitude, ground]], color: '#ffffff', width: 1, dashed: true })
    }
    if (settings.showGimbal) {
      const view = cameraFootprint(aircraft, agl, cameraSpec(draft), lensFocalLength())
      polygons.push({ id: 'aircraft-fov', positions: view.corners.map(point => [point.longitude, point.latitude] as [number, number]), color: '#19be6b' })
      const groundPoint = (point: { longitude: number; latitude: number }): EditorMarker['position'] => [point.longitude, point.latitude, ground]
      view.corners.forEach((point, index) => {
        polylines.push({ id: `aircraft-frustum-${index}`, positions: [[aircraft.longitude, aircraft.latitude, absolute], groundPoint(point)], color: '#19be6b', width: 2, dashed: true })
      })
      polylines.push({ id: 'aircraft-fov-outline', positions: [...view.corners, view.corners[0]].map(point => [point.longitude, point.latitude, null] as EditorMarker['position']), color: '#19be6b', width: 2, dashed: true })
    }
    const anchor = insertionAnchor()
    if (anchor) {
      const anchorHeight = toAbsolute(waypointHeight(draft, anchor))
      const both = absolute !== null && anchorHeight !== null
      polylines.push({ id: 'aircraft-link', positions: [[anchor.longitude, anchor.latitude, both ? anchorHeight : null], [aircraft.longitude, aircraft.latitude, both ? absolute : null]], color: '#ffd166', width: 2, dashed: true })
    }
  }
  if (myPosition.value) markers.push({ id: 'me', position: [myPosition.value.longitude, myPosition.value.latitude, surface('me')], label: '我的位置', color: '#4ea1ff', selected: false, size: 10, draggable: false })
  if (takeoff) markers.push({ id: 'takeoff', position: [takeoff.longitude, takeoff.latitude, ground], label: draft.dock ? draft.dock.name : '起飞点', color: '#ffb020', selected: false, size: 12, draggable: !draft.dock })
  if (draft.templateType === 'waypoint') {
    const points = draft.waypoint.waypoints
    const positions = points.map(point => [point.longitude, point.latitude, toAbsolute(waypointHeight(draft, point))] as EditorMarker['position'])
    const threeD = positions.every(position => position[2] !== null)
    polylines.push({ id: 'route', positions: threeD ? positions : positions.map(position => [position[0], position[1], null]), color: threeD ? '#19be6b' : '#ffd166', width: lineWidth, dashed: !threeD })
    if (takeoff && positions.length) {
      const first = positions[0]
      const climb = toAbsolute(heightMode.value === 'WGS84' ? (takeoff.ellipsoidHeight ?? 0) + draft.missionConfig.takeOffSecurityHeight : draft.missionConfig.takeOffSecurityHeight)
      const connector: EditorPolyline['positions'] = threeD && ground !== null
        ? (draft.missionConfig.flyToWaylineMode === 'safely'
            ? [[takeoff.longitude, takeoff.latitude, ground], [takeoff.longitude, takeoff.latitude, Math.max(climb ?? 0, first[2] ?? 0)], first]
            : [[takeoff.longitude, takeoff.latitude, ground], [takeoff.longitude, takeoff.latitude, climb], first])
        : [[takeoff.longitude, takeoff.latitude, null], [first[0], first[1], null]]
      polylines.push({ id: 'takeoff-path', positions: connector, color: '#ffb020', width: 3, dashed: true })
    }
    points.forEach((point, index) => {
      if (threeD && ground !== null && settings.showDropLines) {
        polylines.push({ id: `drop:${point.id}`, positions: [positions[index], [point.longitude, point.latitude, ground]], color: '#ffffff', width: 1, dashed: true })
        markers.push({ id: `shadow:${point.id}`, position: [point.longitude, point.latitude, surface(point.id)], label: '', color: '#ffffff', selected: false, size: 4, draggable: false })
      }
      if (settings.showWaypoints) {
        const low = nearGround.value.some(issue => issue.index === index)
        markers.push({
          id: point.id,
          position: threeD ? positions[index] : [point.longitude, point.latitude, null],
          label: String(index + 1),
          color: point.isRisky ? '#ff6b6b' : low ? '#ff9f1a' : '#2d8cf0',
          selected: selectedIds.value.includes(point.id),
          size: 9,
          draggable: true,
          shape: 'pin',
        })
      }
      const heading = point.useGlobalHeadingParam ? null : point.headingParam
      if (heading?.waypointHeadingMode === 'towardPOI' && heading.waypointPoiPoint) {
        markers.push({ id: `poi:${point.id}`, position: [heading.waypointPoiPoint[0], heading.waypointPoiPoint[1], surface(`poi:${point.id}`)], label: `POI ${index + 1}`, color: '#c77dff', selected: false, size: 8, draggable: true })
      }
    })
    const globalPoi = draft.waypoint.globalHeadingParam.waypointPoiPoint
    if (globalPoi && draft.waypoint.globalHeadingParam.waypointHeadingMode === 'towardPOI') {
      markers.push({ id: 'poi:global', position: [globalPoi[0], globalPoi[1], surface('poi:global')], label: '兴趣点', color: '#c77dff', selected: false, size: 9, draggable: true })
    }
  } else {
    const vertices = draft.mapping.vertices
    if (vertices.length >= 3) polygons.push({ id: 'area', positions: vertices.map(vertex => [vertex.longitude, vertex.latitude]), color: '#4ea1ff' })
    else if (vertices.length === 2) polylines.push({ id: 'area-line', positions: vertices.map(vertex => [vertex.longitude, vertex.latitude, null] as EditorMarker['position']), color: '#4ea1ff', width: 2, dashed: true })
    vertices.forEach((vertex, index) => {
      markers.push({ id: vertex.id, position: [vertex.longitude, vertex.latitude, surface(vertex.id)], label: String(index + 1), color: '#4ea1ff', selected: selectedIds.value.includes(vertex.id), size: 8, draggable: true })
    })
    const route = generated.value
    if (route) {
      const height = toAbsolute(draft.mapping.height)
      polylines.push({ id: 'route', positions: route.waypoints.map(point => [point.longitude, point.latitude, height] as EditorMarker['position']), color: height === null ? '#ffd166' : '#19be6b', width: settings.thickLines ? 5 : 3, dashed: height === null })
      if (draft.mapping.margin > 0) polylines.push({ id: 'boundary', positions: [...route.boundary, route.boundary[0]].map(point => [point.longitude, point.latitude, null] as EditorMarker['position']), color: '#4ea1ff', width: 1, dashed: true })
      if (takeoff && route.waypoints.length) polylines.push({ id: 'takeoff-path', positions: [[takeoff.longitude, takeoff.latitude, null], [route.waypoints[0].longitude, route.waypoints[0].latitude, null]], color: '#ffb020', width: 2, dashed: true })
    }
  }
  return { markers, polylines, polygons }
})

const PREVIEW_ZOOM_FACTOR = 7
function lensFocalLength (): number {
  const camera = cameraSpec(draft)
  return aircraft.lens === 'zoom' ? camera.focalLength * PREVIEW_ZOOM_FACTOR : camera.focalLength
}

/** Waypoint after which the next Space press inserts. */
function insertionAnchor (): DraftWaypoint | undefined {
  return selected.value.length === 1 ? selected.value[0] : draft.waypoint.waypoints[draft.waypoint.waypoints.length - 1]
}

watch(shapes, value => setCesiumEditorShapes(value, false))

const trailPositions = computed<EditorMarker['position'][] | null>(() => {
  if (trail.value.length < 1 || !aircraft.active) return null
  const points = [...trail.value, [aircraft.longitude, aircraft.latitude, aircraft.height] as [number, number, number]]
  const absolute = points.map(point => [point[0], point[1], toScene(absoluteHeight(draft, point[2], heightMode.value))] as EditorMarker['position'])
  return absolute.every(point => point[2] !== null) ? absolute : absolute.map(point => [point[0], point[1], null] as EditorMarker['position'])
})
watch(trailPositions, value => setCesiumTrail(value))
watch([tool, toolHint, () => shapes.value.markers.map(marker => marker.id).join(',')], () => registerInteraction())

// ---------------------------------------------------------------------------
// Lifecycle

onMounted(() => {
  cesiumMapState.editorFullscreen = true
  // The saved template.kml carries EGM96 altitudes, so fetch the geoid grid now
  // rather than stalling the first save on it.
  preloadGeoidModel()
  setCesiumEditorShapes(shapes.value, shapes.value.markers.length > 0)
  registerInteraction()
  window.addEventListener('keydown', onWindowKeydown)
  window.addEventListener('keyup', onWindowKeyup)
  window.addEventListener('blur', releaseKeys)
  groundTimer = setInterval(sampleGround, 1000)
  sampleGround()
  scheduleWaypointGround()
  if (draft.templateType === 'waypoint') {
    if (draft.missionConfig.takeOffRefPoint || draft.waypoint.waypoints.length) takeOff()
    else {
      tool.value = 'takeoff'
      message.info(draft.dock ? '机场尚未上报位置，请先在地图上点击设置参考起飞点。' : '请先在地图上点击设置参考起飞点，虚拟飞行器将从那里起飞。', 5)
    }
  } else if (!draft.mapping.vertices.length) {
    tool.value = 'add'
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onWindowKeydown)
  window.removeEventListener('keyup', onWindowKeyup)
  window.removeEventListener('blur', releaseKeys)
  if (groundTimer) clearInterval(groundTimer)
  if (groundSampleTimer) clearTimeout(groundSampleTimer)
  landAircraft()
  clearCesiumEditor()
  cesiumMapState.editorFullscreen = false
})

/** Per-tick readings: camera heading, terrain availability and the ground under the aircraft. */
function sampleGround () {
  const api = cesiumSceneApi.value
  if (!api) return
  cameraHeading.value = api.cameraHeading()
  terrainAvailable.value = api.hasTerrain()
  samplePadTerrain()
  if (!aircraft.active) return
  if (!terrainAvailable.value) { aircraftGround.value = 0; return }
  // The rendered mesh is coarse when the camera is far away; the detailed tile
  // sample is what the AGL readout and the camera footprint should use.
  const { longitude, latitude } = aircraft
  api.sampleTerrain(longitude, latitude).then(height => {
    if (height !== null && aircraft.active && haversineDistance(aircraft, { longitude, latitude }) < 50) aircraftGround.value = height
  }).catch(() => {})
}

/** Terrain under the takeoff point; re-sampled when the pad moves or terrain arrives. */
function samplePadTerrain () {
  const api = cesiumSceneApi.value
  const pad = draft.missionConfig.takeOffRefPoint
  if (!api || !pad || !terrainAvailable.value) { padTerrain.value = null; padTerrainKey = ''; return }
  const key = `${pad.longitude.toFixed(7)},${pad.latitude.toFixed(7)}`
  if (key === padTerrainKey && padTerrain.value !== null) return
  padTerrainKey = key
  api.sampleTerrain(pad.longitude, pad.latitude).then(height => {
    if (padTerrainKey === key) padTerrain.value = height
  }).catch(() => {})
}

/**
 * Everything drawn on the ground: waypoint shadows, interest points, area
 * vertices and the operator's position. Cesium cannot draw a true point
 * clamped to terrain; it substitutes a rasterised billboard that comes out as
 * a large blurred disc over real terrain. These markers are therefore placed
 * at the sampled terrain height instead (crisp point primitives), and only
 * fall back to clamping until their sample arrives.
 */
const groundTargets = computed<Array<{ id: string; longitude: number; latitude: number }>>(() => {
  const targets: Array<{ id: string; longitude: number; latitude: number }> = []
  if (draft.templateType === 'waypoint') {
    draft.waypoint.waypoints.forEach(point => {
      targets.push({ id: point.id, longitude: point.longitude, latitude: point.latitude })
      const poi = point.useGlobalHeadingParam ? null : point.headingParam.waypointPoiPoint
      if (poi && point.headingParam.waypointHeadingMode === 'towardPOI') targets.push({ id: `poi:${point.id}`, longitude: poi[0], latitude: poi[1] })
    })
    const globalPoi = draft.waypoint.globalHeadingParam.waypointPoiPoint
    if (globalPoi) targets.push({ id: 'poi:global', longitude: globalPoi[0], latitude: globalPoi[1] })
  } else {
    draft.mapping.vertices.forEach(vertex => targets.push({ id: vertex.id, longitude: vertex.longitude, latitude: vertex.latitude }))
  }
  if (myPosition.value) targets.push({ id: 'me', longitude: myPosition.value.longitude, latitude: myPosition.value.latitude })
  return targets
})

/**
 * Terrain under each ground target from the most detailed tiles, so 近地提示
 * and the ground markers do not depend on how far the camera happens to be.
 * Samples are cached per position and only re-requested when a point moves.
 */
function scheduleWaypointGround () {
  if (groundSampleTimer) clearTimeout(groundSampleTimer)
  groundSampleTimer = setTimeout(sampleWaypointGround, 250)
}

async function sampleWaypointGround () {
  const api = cesiumSceneApi.value
  if (!api) return
  const pending = groundTargets.value
    .map(point => ({ id: point.id, key: `${point.longitude.toFixed(7)},${point.latitude.toFixed(7)}`, longitude: point.longitude, latitude: point.latitude }))
    .filter(point => groundCache.get(point.id)?.key !== point.key)
  if (!pending.length) { publishGround(); return }
  const samples = await Promise.all(pending.map(point => api.sampleTerrain(point.longitude, point.latitude).catch(() => null)))
  pending.forEach((point, index) => groundCache.set(point.id, { key: point.key, height: samples[index] }))
  publishGround()
}

function publishGround () {
  const next: Record<string, number | null> = {}
  groundTargets.value.forEach(point => { next[point.id] = groundCache.get(point.id)?.height ?? null })
  groundHeights.value = next
}

/** A pad picked on the map gets the most detailed terrain height once tiles resolve. */
async function refinePadHeight () {
  const api = cesiumSceneApi.value
  const pad = draft.missionConfig.takeOffRefPoint
  // The bare ellipsoid samples 0 everywhere; a pad height picked or reported
  // there is the best figure available and must not be flattened.
  if (!api || !pad || !api.hasTerrain()) return
  const key = `${pad.longitude.toFixed(7)},${pad.latitude.toFixed(7)}`
  const height = await api.sampleTerrain(pad.longitude, pad.latitude).catch(() => null)
  const current = draft.missionConfig.takeOffRefPoint
  if (height === null || !current || `${current.longitude.toFixed(7)},${current.latitude.toFixed(7)}` !== key) return
  if (current.ellipsoidHeight === null || Math.abs(current.ellipsoidHeight - height) > 0.2) current.ellipsoidHeight = Math.round(height * 10) / 10
}

watch(() => groundTargets.value.map(point => `${point.id}:${point.longitude.toFixed(7)},${point.latitude.toFixed(7)}`).join('|'), scheduleWaypointGround)
watch(() => cesiumSceneApi.value, api => { if (api) { scheduleWaypointGround(); sampleGround() } })

// ---------------------------------------------------------------------------
// Virtual aircraft

function padHeight (): number {
  const takeoff = draft.missionConfig.takeOffRefPoint
  const climb = draft.missionConfig.takeOffSecurityHeight
  return heightMode.value === 'WGS84' ? (takeoff?.ellipsoidHeight ?? 0) + climb : climb
}

function takeOff () {
  if (draft.templateType !== 'waypoint') return
  const takeoff = draft.missionConfig.takeOffRefPoint
  const points = draft.waypoint.waypoints
  const start = selected.value.length === 1 ? selected.value[0] : points[points.length - 1]
  const origin = start
    ? { longitude: start.longitude, latitude: start.latitude, height: waypointHeight(draft, start), gimbalPitch: start.gimbalPitchAngle }
    : takeoff
      ? { longitude: takeoff.longitude, latitude: takeoff.latitude, height: padHeight(), gimbalPitch: -30 }
      : null
  if (!origin) {
    message.warning('请先设置参考起飞点（或添加一个航点），虚拟飞行器将从那里起飞。')
    tool.value = 'takeoff'
    return
  }
  Object.assign(aircraft, { active: true, ...origin })
  if (start) {
    const index = points.indexOf(start)
    aircraft.heading = index > 0 ? bearing(points[index - 1], start) : (takeoff ? bearing(takeoff, start) : 0)
    if (!start.useGlobalHeadingParam && start.headingParam.waypointHeadingMode === 'smoothTransition') aircraft.heading = (start.headingParam.waypointHeadingAngle + 360) % 360
    if (!selectedIds.value.length) selectedIds.value = [start.id]
  } else if (points.length) {
    aircraft.heading = bearing(origin, points[0])
  }
  trail.value = [[origin.longitude, origin.latitude, origin.height]]
  tool.value = 'select'
  pressedKeys.value = {}
  lastFrameTime = 0
  cancelAnimationFrame(animationFrame)
  animationFrame = requestAnimationFrame(stepAircraft)
  sampleGround()
  syncCamera(true)
}

function landAircraft () {
  if (!aircraft.active) return
  aircraft.active = false
  cancelAnimationFrame(animationFrame)
  animationFrame = 0
  pressedKeys.value = {}
  followCesiumPosition(null)
  setCesiumTrail(null)
}

function stepAircraft (time: number) {
  if (!aircraft.active) return
  const dt = lastFrameTime ? Math.min(0.1, (time - lastFrameTime) / 1000) : 0
  lastFrameTime = time
  const keys = pressedKeys.value
  const controls = {
    forward: (keys.w ? 1 : 0) - (keys.s ? 1 : 0),
    right: (keys.d ? 1 : 0) - (keys.a ? 1 : 0),
    climb: (keys.c ? 1 : 0) - (keys.z ? 1 : 0),
    yaw: (keys.e ? 1 : 0) - (keys.q ? 1 : 0),
    gimbal: (keys.i ? 1 : 0) - (keys.k ? 1 : 0),
  }
  if (dt > 0 && (controls.forward || controls.right || controls.climb || controls.yaw || controls.gimbal)) {
    const next = stepVirtualAircraft(aircraft, controls, dt, {
      speed: AIRCRAFT_SPEED_MODES[aircraft.speedMode].speed,
      heightRange: heightRange.value,
      gimbalPitchRange: droneProfile.value?.gimbalPitchRange || [-90, 30],
    })
    Object.assign(aircraft, next)
    if (controls.forward || controls.right || controls.climb) recordTrail()
    syncCamera(false)
  }
  animationFrame = requestAnimationFrame(stepAircraft)
}

function recordTrail () {
  const last = trail.value[trail.value.length - 1]
  if (last && haversineDistance({ longitude: last[0], latitude: last[1] }, aircraft) < 0.5 && Math.abs(last[2] - aircraft.height) < 0.5) return
  trail.value = [...trail.value, [aircraft.longitude, aircraft.latitude, aircraft.height]]
}

/** Keep the map camera on the aircraft according to the view mode. */
function syncCamera (jump: boolean) {
  if (!aircraft.active) return
  const absolute = toScene(absoluteHeight(draft, aircraft.height, heightMode.value)) ?? aircraftAgl.value
  if (viewMode.value === 'fpv') {
    cesiumSceneApi.value?.lookFrom(aircraft.longitude, aircraft.latitude, absolute + 0.5, aircraft.heading, aircraft.gimbalPitch)
  } else if (viewMode.value === 'follow') {
    followCesiumPosition({ longitude: aircraft.longitude, latitude: aircraft.latitude, height: absolute })
  } else if (jump) {
    flyCesiumTo(aircraft.longitude, aircraft.latitude, 250)
  }
}

function setViewMode (mode: ViewMode) {
  viewMode.value = mode
  if (mode !== 'follow') followCesiumPosition(null)
  if (mode === 'follow' && settings.flat) cesiumSceneApi.value?.tilt('top')
  if (mode !== 'fpv' && !settings.flat) cesiumSceneApi.value?.tilt('oblique')
  syncCamera(true)
}

function setFlat (flat: boolean) {
  settings.flat = flat
  if (viewMode.value === 'fpv') viewMode.value = 'follow'
  cesiumSceneApi.value?.tilt(flat ? 'top' : 'oblique')
  syncCamera(false)
}

function toggleSpeedMode () { aircraft.speedMode = AIRCRAFT_SPEED_MODES[aircraft.speedMode].next }
function setGimbalPitch (pitch: number) {
  const range = droneProfile.value?.gimbalPitchRange || [-90, 30]
  aircraft.gimbalPitch = Math.min(range[1], Math.max(range[0], Math.round(pitch)))
  syncCamera(false)
}
function setAircraftLens (lens: PayloadLens) { aircraft.lens = lens }
function setAircraftHeight (height: number) {
  aircraft.height = Math.min(heightRange.value[1], Math.max(heightRange.value[0], height))
  recordTrail()
  syncCamera(false)
}
function setAircraftHeading (heading: number) {
  aircraft.heading = ((heading % 360) + 360) % 360
  syncCamera(false)
}

/** Place the aircraft on a waypoint, restoring the attitude recorded there. */
function moveAircraftTo (target: DraftWaypoint) {
  if (!aircraft.active) return
  const index = draft.waypoint.waypoints.indexOf(target)
  aircraft.longitude = target.longitude
  aircraft.latitude = target.latitude
  aircraft.height = waypointHeight(draft, target)
  aircraft.gimbalPitch = target.gimbalPitchAngle
  const heading = target.useGlobalHeadingParam ? draft.waypoint.globalHeadingParam : target.headingParam
  if (heading.waypointHeadingMode === 'smoothTransition') aircraft.heading = (heading.waypointHeadingAngle + 360) % 360
  else if (heading.waypointHeadingMode === 'towardPOI' && heading.waypointPoiPoint) aircraft.heading = bearing(target, { longitude: heading.waypointPoiPoint[0], latitude: heading.waypointPoiPoint[1] })
  else if (index > 0) aircraft.heading = bearing(draft.waypoint.waypoints[index - 1], target)
  trail.value = [[aircraft.longitude, aircraft.latitude, aircraft.height]]
  sampleGround()
  syncCamera(true)
}

function moveAircraftToSelected () {
  const target = selected.value[0]
  if (target) moveAircraftTo(target)
}

/** The waypoint the aircraft is parked on, or a new one inserted after the anchor. */
function waypointForAircraft (): DraftWaypoint {
  const parked = selected.value.length === 1 && aircraftAtWaypoint(draft, aircraft, selected.value[0]) ? selected.value[0] : null
  if (parked) return parked
  const waypoint = createWaypoint(draft, aircraft.longitude, aircraft.latitude, Math.round(aircraft.height * 10) / 10)
  applyAttitude(waypoint)
  const anchor = insertionAnchor()
  const index = anchor ? draft.waypoint.waypoints.indexOf(anchor) + 1 : draft.waypoint.waypoints.length
  draft.waypoint.waypoints.splice(index, 0, waypoint)
  selectedIds.value = [waypoint.id]
  activeActionIndex.value = -1
  return waypoint
}

function applyAttitude (waypoint: DraftWaypoint) {
  waypoint.gimbalPitchAngle = Math.round(aircraft.gimbalPitch)
  if (!settings.recordAttitude) return
  waypoint.useGlobalHeadingParam = false
  waypoint.headingParam = { ...waypoint.headingParam, waypointHeadingMode: 'smoothTransition', waypointHeadingAngle: Math.round(signedHeading(aircraft.heading)) }
}

function addWaypointAtAircraft () {
  if (!aircraft.active) return
  mutate(() => {
    const waypoint = createWaypoint(draft, aircraft.longitude, aircraft.latitude, Math.round(aircraft.height * 10) / 10)
    applyAttitude(waypoint)
    const anchor = insertionAnchor()
    const index = anchor ? draft.waypoint.waypoints.indexOf(anchor) + 1 : draft.waypoint.waypoints.length
    draft.waypoint.waypoints.splice(index, 0, waypoint)
    selectedIds.value = [waypoint.id]
    activeActionIndex.value = -1
  })
}

function orientedShotAtAircraft () {
  if (!aircraft.active) return
  mutate(() => {
    const waypoint = waypointForAircraft()
    const action = createAction('orientedShoot', draft, waypoint)
    action.params.gimbalPitchRotateAngle = Math.round(aircraft.gimbalPitch)
    action.params.aircraftHeading = Math.round(signedHeading(aircraft.heading))
    action.params.gimbalYawRotateAngle = action.params.aircraftHeading
    if (aircraft.lens === 'zoom' && payloadProfile.value?.zoomRange) action.params.focalLength = payloadProfile.value.zoomRange[0]
    action.params.payloadLensIndex = aircraft.lens
    action.params.useGlobalPayloadLensIndex = false
    waypoint.actions.push(action)
    activeActionIndex.value = waypoint.actions.length - 1
  })
  message.success('已按当前姿态添加定向拍照')
}

function panoShotAtAircraft () {
  if (!aircraft.active) return
  if (droneProfile.value && !droneProfile.value.supportsPano) { message.warning(`${droneProfile.value.name} 不支持全景拍照。`); return }
  mutate(() => {
    const waypoint = waypointForAircraft()
    waypoint.actions.push(createAction('panoShot', draft, waypoint))
    activeActionIndex.value = waypoint.actions.length - 1
  })
}

function recordAttitudeAtAircraft () {
  if (!aircraft.active) return
  mutate(() => {
    const waypoint = waypointForAircraft()
    const yaw = createAction('rotateYaw', draft, waypoint)
    yaw.params.aircraftHeading = Math.round(signedHeading(aircraft.heading))
    const gimbal = createAction('gimbalRotate', draft, waypoint)
    gimbal.params.gimbalPitchRotateEnable = true
    gimbal.params.gimbalPitchRotateAngle = Math.round(aircraft.gimbalPitch)
    gimbal.params.gimbalYawRotateEnable = true
    gimbal.params.gimbalYawRotateAngle = yaw.params.aircraftHeading
    waypoint.actions.push(yaw, gimbal)
    activeActionIndex.value = waypoint.actions.length - 1
  })
  message.success('已记录当前飞行器偏航与云台姿态')
}

function actionAtAircraft (type: WaypointActionType) {
  if (!aircraft.active) return
  mutate(() => {
    const waypoint = waypointForAircraft()
    const action = createAction(type, draft, waypoint)
    if (type === 'zoom' && payloadProfile.value?.zoomRange) action.params.focalLength = payloadProfile.value.zoomRange[0]
    waypoint.actions.push(action)
    activeActionIndex.value = waypoint.actions.length - 1
  })
}

function releaseKeys () { pressedKeys.value = {} }

function onWindowKeyup (event: KeyboardEvent) {
  const key = event.key.toLowerCase()
  if (pressedKeys.value[key]) pressedKeys.value = { ...pressedKeys.value, [key]: false }
}

/** Returns true when the key was consumed by the virtual aircraft. */
function onAircraftKeydown (event: KeyboardEvent): boolean {
  if (!aircraft.active) return false
  const key = event.key.toLowerCase()
  if (key === ' ') { if (!event.repeat) addWaypointAtAircraft(); return true }
  if (key === 'f') { if (!event.repeat) orientedShotAtAircraft(); return true }
  if (key === 'x') { if (!event.repeat) toggleSpeedMode(); return true }
  if (key === 'v') { if (!event.repeat) setViewMode(viewMode.value === 'follow' ? 'free' : 'follow'); return true }
  if (key === '`' || key === '~') { if (!event.repeat) setViewMode(viewMode.value === 'fpv' ? 'follow' : 'fpv'); return true }
  if (key === '1') { aircraft.lens = 'wide'; return true }
  if (key === '2') { if (payloadProfile.value?.lenses.includes('zoom')) aircraft.lens = 'zoom'; return true }
  if ('wasdqezcik'.includes(key) && key.length === 1) {
    if (!pressedKeys.value[key]) pressedKeys.value = { ...pressedKeys.value, [key]: true }
    return true
  }
  return false
}

function registerInteraction () {
  const draggable = shapes.value.markers.filter(marker => marker.draggable).map(marker => marker.id)
  setCesiumEditorInteraction(onMapEvent, draggable, toolHint.value)
}

// ---------------------------------------------------------------------------
// History

function snapshot (): string {
  return JSON.stringify({ draft, selectedIds: selectedIds.value })
}

function commit () {
  dirty.value = true
  draft.updateTime = Date.now()
}

function pushHistory (state = snapshot()) {
  history.value.push(state)
  if (history.value.length > 60) history.value.shift()
}

function mutate (change: () => void) {
  pushHistory()
  change()
  commit()
}

function undo () {
  const state = history.value.pop()
  if (!state) return
  const parsed = JSON.parse(state)
  Object.assign(draft, parsed.draft)
  selectedIds.value = parsed.selectedIds
  activeActionIndex.value = -1
  commit()
}

// ---------------------------------------------------------------------------
// Map interaction

function setTool (next: EditorTool) {
  tool.value = tool.value === next && next !== 'select' ? 'select' : next
}

function pickPoi (target: 'global' | 'selected') {
  poiTarget.value = target
  tool.value = 'poi'
  routeSettingsOpen.value = false
}

function onMapEvent (event: EditorEvent) {
  if (event.type === 'click') return onMapClick(event)
  if (event.type === 'rightClick') return onMapRightClick(event)
  if (event.type === 'dragStart') {
    dragSnapshot = snapshot()
    if (event.markerId && !event.markerId.startsWith('poi:') && !event.markerId.startsWith('shadow:') && !event.markerId.startsWith('aircraft') && event.markerId !== 'takeoff') selectedIds.value = [event.markerId]
    return
  }
  if (event.type === 'drag' || event.type === 'dragEnd') {
    if (!event.markerId) return
    moveMarker(event.markerId, event)
    if (event.type === 'dragEnd') {
      if (dragSnapshot) pushHistory(dragSnapshot)
      dragSnapshot = null
      commit()
      const waypoint = draft.waypoint.waypoints.find(item => item.id === event.markerId)
      if (waypoint) moveAircraftTo(waypoint)
      if (event.markerId === 'takeoff') refinePadHeight()
    }
  }
}

function moveMarker (markerId: string, event: EditorEvent) {
  if (markerId === 'takeoff') {
    draft.missionConfig.takeOffRefPoint = { longitude: event.longitude, latitude: event.latitude, ellipsoidHeight: fromScene(event.height), aglHeight: draft.missionConfig.takeOffRefPoint?.aglHeight ?? 0 }
    return
  }
  if (markerId === 'poi:global') {
    draft.waypoint.globalHeadingParam.waypointPoiPoint = [event.longitude, event.latitude]
    return
  }
  if (markerId.startsWith('poi:')) {
    const waypoint = draft.waypoint.waypoints.find(item => item.id === markerId.slice(4))
    if (waypoint) waypoint.headingParam.waypointPoiPoint = [event.longitude, event.latitude]
    return
  }
  const waypoint = draft.waypoint.waypoints.find(item => item.id === markerId)
  if (waypoint) { waypoint.longitude = event.longitude; waypoint.latitude = event.latitude; return }
  const vertex = draft.mapping.vertices.find(item => item.id === markerId)
  if (vertex) { vertex.longitude = event.longitude; vertex.latitude = event.latitude }
}

function onMapClick (event: EditorEvent) {
  if (event.markerId) {
    if (event.markerId === 'takeoff' || event.markerId === 'me' || event.markerId.startsWith('aircraft') || event.markerId.startsWith('shadow:') || event.markerId.startsWith('poi:')) return
    if (draft.templateType === 'waypoint') selectWaypoint(event.markerId, false)
    else selectedIds.value = [event.markerId]
    return
  }
  if (tool.value === 'takeoff') {
    const padHeight = fromScene(event.height)
    // A point picked on the map is on the ground, so its AGL height is 0.
    mutate(() => { draft.missionConfig.takeOffRefPoint = { longitude: event.longitude, latitude: event.latitude, ellipsoidHeight: padHeight, aglHeight: 0 }; draft.dock = null })
    tool.value = 'select'
    message.success(terrainAvailable.value ? `参考起飞点已设置，地面椭球高 ${padHeight.toFixed(1)} m` : '参考起飞点已设置；当前未配置真实地形，高度按参考起飞点平面显示')
    refinePadHeight()
    if (draft.templateType === 'waypoint' && !aircraft.active) takeOff()
    return
  }
  if (tool.value === 'poi') {
    mutate(() => {
      if (poiTarget.value === 'global' || !selected.value.length) {
        draft.waypoint.globalHeadingParam.waypointPoiPoint = [event.longitude, event.latitude]
        draft.waypoint.globalHeadingParam.waypointHeadingMode = 'towardPOI'
      } else {
        selected.value.forEach(waypoint => {
          waypoint.useGlobalHeadingParam = false
          waypoint.headingParam.waypointHeadingMode = 'towardPOI'
          waypoint.headingParam.waypointPoiPoint = [event.longitude, event.latitude]
        })
      }
    })
    tool.value = 'select'
    return
  }
  if (tool.value !== 'add') return
  if (draft.templateType === 'waypoint') {
    mutate(() => {
      const waypoint = createWaypoint(draft, event.longitude, event.latitude)
      const anchor = insertionAnchor()
      const index = anchor ? draft.waypoint.waypoints.indexOf(anchor) + 1 : draft.waypoint.waypoints.length
      draft.waypoint.waypoints.splice(index, 0, waypoint)
      selectedIds.value = [waypoint.id]
      activeActionIndex.value = -1
    })
  } else {
    addVertexAt(event.longitude, event.latitude)
  }
}

function onMapRightClick (event: EditorEvent) {
  if (!event.markerId || draft.templateType !== 'waypoint') return
  const waypoint = draft.waypoint.waypoints.find(item => item.id === event.markerId)
  if (!waypoint) return
  selectWaypoint(waypoint.id, false)
  if (droneProfile.value?.supportsRisky) toggleRisky(waypoint.id)
}

function addVertexAt (longitude: number, latitude: number) {
  mutate(() => {
    const vertex = createVertex(longitude, latitude)
    const anchor = selectedIds.value.length === 1 ? draft.mapping.vertices.findIndex(item => item.id === selectedIds.value[0]) : -1
    draft.mapping.vertices.splice(anchor >= 0 ? anchor + 1 : draft.mapping.vertices.length, 0, vertex)
    selectedIds.value = [vertex.id]
  })
}

function locateMe () {
  if (!navigator.geolocation) { message.warning('当前浏览器不支持定位。'); return }
  navigator.geolocation.getCurrentPosition(position => {
    const { longitude, latitude, accuracy } = position.coords
    myPosition.value = { longitude, latitude }
    setViewMode('free')
    flyCesiumTo(longitude, latitude, Math.max(300, accuracy * 2))
    message.success(`已定位到当前位置（精度约 ${Math.round(accuracy)} m）`)
  }, error => {
    const reasons: Record<number, string> = { 1: '浏览器拒绝了定位权限，请在地址栏允许定位后重试。', 2: '无法获取当前位置。', 3: '定位超时，请重试。' }
    message.error(reasons[error.code] || '定位失败。')
  }, { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 })
}

function fitRoute () {
  if (viewMode.value !== 'free') setViewMode('free')
  focusCesiumEditor()
}

function flyToSelected () {
  const target = selected.value[0]
  if (!target) return
  if (aircraft.active) moveAircraftTo(target)
  else flyCesiumTo(target.longitude, target.latitude, 200)
}

function zoom (direction: number) { cesiumSceneApi.value?.zoom(direction) }
function resetNorth () { cesiumSceneApi.value?.view('north'); cameraHeading.value = 0 }

// ---------------------------------------------------------------------------
// Waypoint list operations

function selectWaypoint (id: string, range = false) {
  const points = draft.waypoint.waypoints
  if (range && selectedIds.value.length) {
    const last = points.findIndex(item => item.id === selectedIds.value[selectedIds.value.length - 1])
    const current = points.findIndex(item => item.id === id)
    const [from, to] = last < current ? [last, current] : [current, last]
    selectedIds.value = points.slice(from, to + 1).map(item => item.id)
  } else {
    selectedIds.value = [id]
    const target = points.find(item => item.id === id)
    if (target) moveAircraftTo(target)
  }
  activeActionIndex.value = -1
  rightPanelOpen.value = true
}

function selectByOffset (offset: number) {
  const list = draft.templateType === 'waypoint' ? draft.waypoint.waypoints : draft.mapping.vertices
  if (!list.length) return
  const currentIndex = list.findIndex(item => item.id === selectedIds.value[selectedIds.value.length - 1])
  const next = currentIndex < 0 ? (offset > 0 ? 0 : list.length - 1) : Math.min(list.length - 1, Math.max(0, currentIndex + offset))
  if (draft.templateType === 'waypoint') selectWaypoint(list[next].id, false)
  else selectedIds.value = [list[next].id]
}

function selectActionByOffset (offset: number) {
  const target = selected.value[0]
  if (!target) return
  activeActionIndex.value = Math.min(target.actions.length - 1, Math.max(-1, activeActionIndex.value + offset))
}

function reorderWaypoints (from: number, to: number) {
  const points = draft.waypoint.waypoints
  if (from === to || from < 0 || to < 0 || from >= points.length || to >= points.length) return
  mutate(() => {
    const [moved] = points.splice(from, 1)
    points.splice(to, 0, moved)
  })
}

function removeWaypoint (id: string) {
  mutate(() => {
    draft.waypoint.waypoints = draft.waypoint.waypoints.filter(item => item.id !== id)
    selectedIds.value = selectedIds.value.filter(item => item !== id)
    activeActionIndex.value = -1
  })
}

function removeSelected () {
  const ids = [...selectedIds.value]
  if (!ids.length) return
  if (draft.templateType === 'waypoint') mutate(() => { draft.waypoint.waypoints = draft.waypoint.waypoints.filter(item => !ids.includes(item.id)); selectedIds.value = [] })
  else mutate(() => { draft.mapping.vertices = draft.mapping.vertices.filter(item => !ids.includes(item.id)); selectedIds.value = []; draft.mapping.startVertexIndex = 0 })
  activeActionIndex.value = -1
}

function removeVertex (id: string) {
  mutate(() => {
    draft.mapping.vertices = draft.mapping.vertices.filter(item => item.id !== id)
    selectedIds.value = selectedIds.value.filter(item => item !== id)
    draft.mapping.startVertexIndex = Math.min(draft.mapping.startVertexIndex, Math.max(0, draft.mapping.vertices.length - 1))
  })
}

function clearVertices () {
  mutate(() => { draft.mapping.vertices = []; selectedIds.value = []; draft.mapping.startVertexIndex = 0 })
}

function toggleRisky (id: string) {
  const waypoint = draft.waypoint.waypoints.find(item => item.id === id)
  if (waypoint) mutate(() => { waypoint.isRisky = !waypoint.isRisky })
}

function reverseRoute () {
  mutate(() => { draft.waypoint.waypoints = reverseWaypoints(draft.waypoint.waypoints) })
  message.info('航点顺序已反转，请确认动作配对与航线安全。')
}

function copyActions (id: string) {
  const waypoint = draft.waypoint.waypoints.find(item => item.id === id)
  if (!waypoint) return
  actionTemplate.value = waypoint.actions.map(action => ({ ...action, params: { ...action.params } }))
  message.success(`已复制 ${actionTemplate.value.length} 个动作为模板`)
}

function applyActionTemplate (id: string) {
  const targets = selectedIds.value.includes(id) ? selected.value : draft.waypoint.waypoints.filter(item => item.id === id)
  mutate(() => {
    targets.forEach(waypoint => {
      waypoint.actions.push(...actionTemplate.value.map(action => ({ ...createAction(action.type, draft, waypoint), params: { ...action.params } })))
    })
  })
}

function setWaypointField (key: keyof DraftWaypoint, value: unknown) {
  mutate(() => {
    selected.value.forEach(waypoint => {
      (waypoint as any)[key] = value
      if (key === 'height') waypoint.useGlobalHeight = false
      if (key === 'speed') waypoint.useGlobalSpeed = false
    })
  })
  if (key === 'height' || key === 'gimbalPitchAngle') { if (selected.value.length === 1) moveAircraftTo(selected.value[0]) }
}

function setWaypointNested (group: 'turnParam' | 'headingParam', key: string, value: unknown) {
  mutate(() => {
    selected.value.forEach(waypoint => {
      (waypoint[group] as any)[key] = value
      if (group === 'turnParam') waypoint.useGlobalTurnParam = false
      if (group === 'headingParam') waypoint.useGlobalHeadingParam = false
    })
  })
  if (group === 'headingParam' && selected.value.length === 1) moveAircraftTo(selected.value[0])
}

function moveSelectedTo (longitude: number, latitude: number) {
  const target = selected.value[0]
  if (!target || !Number.isFinite(longitude) || !Number.isFinite(latitude)) return
  mutate(() => { target.longitude = longitude; target.latitude = latitude })
  moveAircraftTo(target)
}

function addAction (type: WaypointActionType) {
  mutate(() => { selected.value.forEach(waypoint => waypoint.actions.push(createAction(type, draft, waypoint))) })
  if (selected.value.length === 1) activeActionIndex.value = selected.value[0].actions.length - 1
}

function removeAction (actionId: string) {
  mutate(() => { selected.value.forEach(waypoint => { waypoint.actions = waypoint.actions.filter(action => action.id !== actionId) }) })
  if (selected.value.length === 1) activeActionIndex.value = Math.min(activeActionIndex.value, selected.value[0].actions.length - 1)
}

function moveAction (actionId: string, direction: 1 | -1) {
  mutate(() => {
    selected.value.forEach(waypoint => {
      const index = waypoint.actions.findIndex(action => action.id === actionId)
      const target = index + direction
      if (index < 0 || target < 0 || target >= waypoint.actions.length) return
      const [action] = waypoint.actions.splice(index, 1)
      waypoint.actions.splice(target, 0, action)
      if (selected.value.length === 1) activeActionIndex.value = target
    })
  })
}

function updateAction (actionId: string, params: Record<string, string | number | boolean>) {
  mutate(() => {
    selected.value.forEach(waypoint => {
      const action = waypoint.actions.find(item => item.id === actionId)
      if (action) action.params = params
    })
  })
}

function focusIssue (index: number) {
  const target = draft.templateType === 'waypoint' ? draft.waypoint.waypoints[index] : draft.mapping.vertices[index]
  if (!target) return
  if (draft.templateType === 'waypoint') selectWaypoint(target.id, false)
  else selectedIds.value = [target.id]
}

function setName (value: string) {
  const name = sanitizeWaylineName(value)
  if (!name) return
  draft.name = name
  commit()
}

function startRename () {
  renaming.value = true
  nextTick(() => { renameInput.value?.focus(); renameInput.value?.select() })
}

function finishRename () {
  if (!renaming.value) return
  renaming.value = false
  if (renameInput.value) setName(renameInput.value.value)
}

function setHeightMode (mode: EditorHeightMode) {
  if (mode === heightMode.value) return
  mutate(() => {
    const before = aircraft.active ? absoluteHeight(draft, aircraft.height, heightMode.value) : null
    convertHeightMode(draft, mode)
    if (aircraft.active) {
      const pad = draft.missionConfig.takeOffRefPoint?.ellipsoidHeight ?? null
      if (before !== null) aircraft.height = mode === 'WGS84' ? before : before - (pad ?? 0)
    }
  })
  trail.value = aircraft.active ? [[aircraft.longitude, aircraft.latitude, aircraft.height]] : []
}

// ---------------------------------------------------------------------------
// Keyboard

function onWindowKeydown (event: KeyboardEvent) {
  const target = event.target as HTMLElement | null
  if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)) return
  if (shortcutsOpen.value) { if (event.key === 'Escape' || event.key === '?') shortcutsOpen.value = false; return }
  if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') { event.preventDefault(); undo(); return }
  if (event.key === '?') { shortcutsOpen.value = true; return }
  if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); selectByOffset(event.key === 'ArrowLeft' ? -1 : 1); return }
  if (event.key === 'ArrowUp' || event.key === 'ArrowDown') { event.preventDefault(); selectActionByOffset(event.key === 'ArrowUp' ? -1 : 1); return }
  if ((event.key === 'Delete' || event.key === 'Backspace') && selectedIds.value.length) { event.preventDefault(); removeSelected(); return }
  // Escape only backs out of panels and tools; the simulation ends from its button.
  if (event.key === 'Escape') { tool.value = 'select'; routeSettingsOpen.value = false; rightPanelOpen.value = false; return }
  if (onAircraftKeydown(event)) event.preventDefault()
}

// ---------------------------------------------------------------------------
// Docks as reference takeoff point

async function loadDocks () {
  if (loadingDocks.value) return
  loadingDocks.value = true
  try {
    const result = await getBindingDevices(workspaceId, { page: 1, page_size: 100, total: 0 }, EDeviceTypeName.Dock)
    if (result.code === 0) docks.value = result.data.list.map(device => buildDockOption(device, store.state.deviceState.dockInfo[device.device_sn]?.basic_osd))
  } finally {
    loadingDocks.value = false
  }
}

function useDock (sn: string) {
  const option = docks.value.find(dock => dock.sn === sn)
  const osd = store.state.deviceState.dockInfo[sn]?.basic_osd
  const position = osd && Number.isFinite(osd.longitude) && Number.isFinite(osd.latitude) && !(osd.longitude === 0 && osd.latitude === 0)
    ? { longitude: osd.longitude, latitude: osd.latitude, ellipsoidHeight: Number.isFinite(osd.height) ? osd.height : null }
    : option?.position || null
  if (!position) { message.warning('机场当前不在线或未上报位置，无法作为参考起飞点。'); return }
  mutate(() => {
    draft.missionConfig.takeOffRefPoint = { ...position }
    if (option) draft.dock = { sn: option.sn, name: option.name, modelKey: option.modelKey }
  })
  routeSettingsOpen.value = false
  if (draft.templateType === 'waypoint' && !aircraft.active) takeOff()
  else fitRoute()
}

function clearTakeoff () {
  mutate(() => { draft.missionConfig.takeOffRefPoint = null; draft.dock = null })
}

// ---------------------------------------------------------------------------
// Save / export / close

async function buildFile () {
  const name = sanitizeWaylineName(draft.name)
  if (!name) throw new Error('请输入航线名称。')
  draft.name = name
  draft.updateTime = Date.now()
  await loadGeoidModel().catch(() => message.warning('EGM96 高程基准数据加载失败，本次保存将省略参考起飞点海拔高。'))
  return buildKmzFile(draft)
}

async function exportKmz () {
  try {
    const file = await buildFile()
    downloadFile(file, file.name)
  } catch (error) {
    message.error((error as Error).message)
  }
}

async function save () {
  if (hasErrors(issues.value)) return
  saving.value = true
  try {
    const file = await buildFile()
    const form = new FormData()
    form.append('file', file, file.name)
    const response = await importKmzFile(workspaceId, form)
    if (response.code !== 0) return
    dirty.value = false
    message.success(`航线“${draft.name}”已保存到航线库`)
    if (props.sourceWaylineId) {
      const sourceId = props.sourceWaylineId
      Modal.confirm({
        title: '删除原航线文件？',
        content: '已按当前内容保存为新的航线文件。原文件若仍被任务计划引用，请保留。',
        okText: '删除原文件',
        okType: 'danger',
        cancelText: '保留',
        onOk: async () => {
          const result = await deleteWaylineFile(workspaceId, sourceId)
          if (result.code === 0) message.success('原航线文件已删除')
          emit('saved')
        },
        onCancel: () => emit('saved'),
      })
      return
    }
    emit('saved')
  } catch (error) {
    message.error((error as Error).message || '保存航线失败')
  } finally {
    saving.value = false
  }
}

function requestClose () {
  if (!dirty.value) { emit('close'); return }
  Modal.confirm({
    title: '放弃未保存的修改？',
    content: '当前航线尚未保存到航线库，返回后修改将丢失。',
    okText: '放弃修改',
    okType: 'danger',
    cancelText: '继续编辑',
    onOk: () => emit('close'),
  })
}

const context: WaylineEditorContext = {
  draft,
  sourceWaylineId: props.sourceWaylineId,
  droneProfile,
  payloadProfile,
  heightMode,
  selectedIds,
  selected,
  activeActionIndex,
  stats,
  issues,
  nearGround,
  groundHeights,
  aircraft,
  aircraftGround,
  aircraftAgl,
  sceneOffset,
  terrainAvailable,
  padTerrain,
  padHeightGap,
  pressedKeys,
  settings,
  tool,
  viewMode,
  cameraHeading,
  docks,
  loadingDocks,
  saving,
  dirty,
  canUndo,
  rightPanelOpen,
  mutate,
  commit,
  undo,
  setName,
  setTool,
  pickPoi,
  setViewMode,
  setFlat,
  setHeightMode,
  selectWaypoint,
  selectByOffset,
  selectActionByOffset,
  reorderWaypoints,
  removeWaypoint,
  removeSelected,
  toggleRisky,
  reverseRoute,
  copyActions,
  applyActionTemplate,
  hasActionTemplate,
  setWaypointField,
  setWaypointNested,
  addAction,
  removeAction,
  moveAction,
  updateAction,
  addVertexAt,
  removeVertex,
  clearVertices,
  takeOff,
  landAircraft,
  addWaypointAtAircraft,
  orientedShotAtAircraft,
  panoShotAtAircraft,
  recordAttitudeAtAircraft,
  actionAtAircraft,
  moveAircraftToSelected,
  toggleSpeedMode,
  setGimbalPitch,
  setAircraftLens,
  setAircraftHeight,
  setAircraftHeading,
  moveSelectedTo,
  loadDocks,
  useDock,
  clearTakeoff,
  refinePadHeight,
  fitRoute,
  flyToSelected,
  zoom,
  resetNorth,
  locateMe,
  save,
  exportKmz,
  requestClose,
}
provide(WAYLINE_EDITOR_KEY, context)
</script>

<style lang="scss">
// The editor is teleported over the shared map, so its styles are global and
// prefixed with wl- to stay out of the way of the map host and antd.
.wl-editor {
  --wl-panel: #1b1c1f;
  --wl-panel-soft: #24262a;
  --wl-border: #35373c;
  --wl-text: #e9eaec;
  --wl-muted: #9a9ea6;
  --wl-accent: #2d8cf0;
  --wl-green: #19be6b;
  --wl-warn: #ff9f1a;
  --wl-danger: #ff4d4f;
  --wl-top: 40px;
  --wl-left: 236px;
  --wl-right: 320px;
  position: fixed; inset: 0; z-index: 120; pointer-events: none; color: var(--wl-text); font-size: 12px; line-height: 1.5;
  * { box-sizing: border-box; }
  button { font-family: inherit; }
}
.wl-topbar {
  position: absolute; top: 0; left: 0; right: 0; height: var(--wl-top); display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; gap: 12px; padding: 0 8px;
  background: #151618; border-bottom: 1px solid var(--wl-border); pointer-events: auto;
  &__left, &__right { display: flex; align-items: center; gap: 4px; }
  &__right { justify-content: flex-end; }
  &__center { display: flex; align-items: center; gap: 14px; min-width: 0; padding: 0 12px; height: 28px; border-radius: 4px; background: #232427; white-space: nowrap; }
  &__name { border: 0; background: transparent; color: #fff; font-size: 13px; font-weight: 600; cursor: text; padding: 0; max-width: 260px; overflow: hidden; text-overflow: ellipsis; }
  &__name:hover { text-decoration: underline dotted; }
  &__rename { width: 240px; height: 24px; border: 1px solid var(--wl-accent); border-radius: 3px; background: #101113; color: #fff; padding: 0 6px; font-size: 13px; outline: none; }
  &__meta { color: var(--wl-muted); display: inline-flex; align-items: center; gap: 4px; }
  &__tag { padding: 0 6px; border-radius: 3px; background: #4d3a12; color: #ffd166; font-size: 11px; }
  &__settings { display: inline-flex; align-items: center; gap: 6px; height: 28px; padding: 0 10px; margin-left: 6px; border: 0; border-radius: 4px; background: #2c2d31; color: #fff; cursor: pointer; }
  &__settings:hover, &__settings.active { background: var(--wl-accent); }
}
.wl-icon-btn {
  display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 28px; border: 0; border-radius: 4px; background: transparent; color: #fff; font-size: 16px; cursor: pointer;
  &:hover:not(:disabled) { background: #ffffff1a; }
  &:disabled { color: #6b6f77; cursor: not-allowed; }
  .ant-badge { color: inherit; }
}
.wl-left { position: absolute; top: var(--wl-top); left: 0; bottom: 0; width: var(--wl-left); background: var(--wl-panel); border-right: 1px solid var(--wl-border); pointer-events: auto; display: flex; flex-direction: column; }
.wl-right { position: absolute; top: var(--wl-top); right: 0; bottom: 0; width: var(--wl-right); background: var(--wl-panel); border-left: 1px solid var(--wl-border); pointer-events: auto; display: flex; flex-direction: column; }
.wl-map { position: absolute; top: var(--wl-top); left: var(--wl-left); right: 0; bottom: 0; pointer-events: none; }
.wl-editor--right-open .wl-map { right: var(--wl-right); }
.wl-warning { position: absolute; top: 10px; left: 12px; pointer-events: auto; }
.wl-warning__chip { display: inline-flex; align-items: center; gap: 6px; max-width: 460px; height: 26px; padding: 0 10px; border: 0; border-radius: 13px; background: var(--wl-warn); color: #1b1300; font-weight: 500; cursor: pointer; white-space: nowrap; overflow: hidden; }
.wl-warning__chip span { overflow: hidden; text-overflow: ellipsis; }
.wl-warning__more { display: inline-block; min-width: 18px; padding: 0 5px; border-radius: 9px; background: #1b1300; color: #ffd166; font-size: 11px; text-align: center; }
.wl-terrain-note { position: absolute; top: 12px; left: 12px; margin: 0; padding: 3px 10px; border-radius: 12px; background: #151618d9; border: 1px solid var(--wl-border); color: #f2d299; pointer-events: auto; cursor: help; }
.wl-terrain-note--below { top: 44px; }
.wl-terrain-note--button { cursor: pointer; }
.wl-terrain-note--button:hover { border-color: #f2d299; }
.wl-tool-hint { position: absolute; top: 12px; left: 50%; transform: translateX(-50%); margin: 0; padding: 4px 12px; border-radius: 13px; background: #151618d9; border: 1px solid var(--wl-border); pointer-events: none; }
.wl-map-actions {
  position: absolute; left: 12px; top: 50%; transform: translateY(-50%); display: flex; flex-direction: column; gap: 6px; pointer-events: auto;
  &__title { color: #fff; font-size: 12px; margin-bottom: 2px; text-shadow: 0 1px 2px #000; }
}
.wl-action {
  display: inline-flex; align-items: center; gap: 8px; height: 34px; padding: 0 10px 0 4px; border: 0; border-radius: 4px; background: #151618cc; color: #fff; cursor: pointer; text-align: left; white-space: nowrap; text-shadow: 0 1px 2px #000;
  &__icon { display: inline-flex; align-items: center; justify-content: center; width: 26px; height: 26px; border-radius: 4px; background: #2c2d31; font-size: 14px; }
  small { display: block; font-size: 10px; color: var(--wl-muted); line-height: 1.1; }
  &:hover:not(:disabled) { background: #2c2d31; }
  &.active { background: var(--wl-accent); }
  &.active .wl-action__icon { background: #ffffff33; }
  &--primary { background: var(--wl-accent); }
  &--primary .wl-action__icon { background: #ffffff33; }
  &--danger:hover:not(:disabled) { background: #5c1f21; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
}
.wl-map-tools {
  position: absolute; right: 12px; bottom: 132px; display: flex; flex-direction: column; align-items: flex-end; gap: 8px; pointer-events: auto;
  &__group { display: flex; flex-direction: column; border-radius: 4px; overflow: hidden; background: #151618d9; border: 1px solid var(--wl-border); }
  &__group button { width: 34px; height: 32px; border: 0; border-bottom: 1px solid var(--wl-border); background: transparent; color: #fff; font-size: 13px; cursor: pointer; }
  &__group button:last-child { border-bottom: 0; }
  &__group button:hover:not(:disabled) { background: #ffffff1a; }
  &__group button.active { color: var(--wl-accent); background: #2d8cf01f; }
  &__group button:disabled { color: #6b6f77; cursor: not-allowed; }
}
.wl-compass { display: flex; flex-direction: column; align-items: center; gap: 2px; border: 0; background: transparent; color: #fff; cursor: pointer; padding: 0; font-size: 10px; text-shadow: 0 1px 2px #000; }
.wl-bottom { position: absolute; left: 0; right: 324px; bottom: 10px; display: flex; justify-content: center; pointer-events: none; }
.wl-hud { pointer-events: auto; }
.wl-camera { position: absolute; right: 12px; bottom: 10px; pointer-events: auto; }
.wl-muted { color: var(--wl-muted); }
.wl-issues { max-width: 420px; max-height: 60vh; overflow: auto; }
.wl-issues h4 { margin: 0 0 6px; color: #fff; font-size: 13px; }
.wl-issues__item { display: flex; gap: 6px; padding: 4px 0; cursor: pointer; line-height: 1.4; }
.wl-issues__item.error { color: #ffb4a9; }
.wl-issues__item.warning { color: #f2d299; }
.wl-issues__item:hover { color: #fff; }

// Shared antd overrides for panels that float over the map.
.wl-popover .ant-popover-inner, .wl-route-settings-dropdown, .wl-menu .ant-dropdown-menu {
  background: #1b1c1f; color: #e9eaec; border: 1px solid #35373c; border-radius: 6px; box-shadow: 0 8px 24px #000a;
}
.wl-popover .ant-popover-inner-content { color: #e9eaec; padding: 10px 12px; }
.wl-popover .ant-popover-arrow { display: none; }
.wl-menu .ant-dropdown-menu-item { color: #e9eaec; }
.wl-menu .ant-dropdown-menu-item:hover { background: #2d8cf0; color: #fff; }
.wl-menu .ant-dropdown-menu-item-disabled { color: #6b6f77 !important; }
.wl-route-settings-dropdown { padding: 0; }
.wl-form {
  .ant-select-selector, .ant-input-number, .ant-input, .ant-input-affix-wrapper { background: #101113 !important; color: #fff !important; border-color: #35373c !important; }
  .ant-select-arrow, .ant-input-number-handler-wrap { color: #c5cbd2; background: #101113; }
  .ant-input-number-handler { border-color: #35373c; }
  .ant-checkbox-wrapper, .ant-radio-button-wrapper, .ant-slider-mark-text { color: #e9eaec; }
  .ant-radio-button-wrapper { background: #101113; border-color: #35373c; }
  .ant-radio-button-wrapper-checked { background: #2d8cf0; border-color: #2d8cf0; color: #fff; }
  .ant-radio-button-wrapper-disabled { color: #6b6f77; background: #1b1c1f; }
  .ant-switch { background: #4a4d55; }
  .ant-switch-checked { background: #2d8cf0; }
  .ant-slider-rail { background: #3a3c42; }
  .ant-slider-track { background: #2d8cf0; }
  .ant-slider-handle { border-color: #2d8cf0; background: #fff; }
  .ant-empty-description { color: #9a9ea6; }
  .ant-select-dropdown { background: #1b1c1f; }
}
.ant-select-dropdown.wl-select { background: #1b1c1f; border: 1px solid #35373c; }
.ant-select-dropdown.wl-select .ant-select-item { color: #e9eaec; }
.ant-select-dropdown.wl-select .ant-select-item-option-active, .ant-select-dropdown.wl-select .ant-select-item-option-selected { background: #2d8cf0; color: #fff; }
</style>
